import { getUnitSourceCategory } from './getUnitSourceCategory'

const FORBIDDEN_CATALOG_STATUSES = new Set(['Drafted', 'Rejected', 'Registered', 'Approved', 'Archived'])

const OPERATIONAL_STATUS_SET = new Set([
    'door close',
    'door closed',
    'door open',
    'agent unit',
    'landlord unit',
    'vacant',
    'occupied',
    'building management',
    'keys in bso office',
    'digital lock'
])

export type ExternalUnitBucket = 'requested' | 'catalog' | 'operational' | 'inactive'

const getAttributes = (unit: any): any => unit?.attributes ?? unit

export const getUnitStatuses = (unit: any): string[] => {
    const statuses = getAttributes(unit)?.Statuses
    return Array.isArray(statuses) ? statuses.filter((status): status is string => typeof status === 'string') : []
}

export const isRentalOrSaleUnit = (unit: any): boolean => {
    const use = getAttributes(unit)?.Marketing?.Use
    return use === 'For rent' || use === 'For sale'
}

export const isBsoRentalOrSaleUnit = (unit: any): boolean =>
    getUnitSourceCategory(unit) === 'bso' && isRentalOrSaleUnit(unit)

export const isExternalUnit = (unit: any): boolean => {
    const source = getUnitSourceCategory(unit)
    return source === 'agent' || source === 'landlord'
}

// This is intentionally a frontend-only mirror of bso-agent-loader's
// `filteredUnits` predicate. My Units already receives the complete unit data.
export const isVisibleInAgentCatalog = (unit: any, now = Date.now()): boolean => {
    const attributes = getAttributes(unit)
    if (!attributes || attributes.Marketing == null || attributes.Property?.data == null) return false

    const statuses = getUnitStatuses(unit)
    // The catalogue loader permits a missing Statuses value, but rejects an explicit empty array.
    if (Array.isArray(attributes.Statuses) && attributes.Statuses.length === 0) return false
    if (attributes.Marketing.Price == null) return false
    if (statuses.some((status) => FORBIDDEN_CATALOG_STATUSES.has(status))) return false
    if (statuses.includes('Sold') && attributes.ManagedBy !== 'BSO') return false

    if (attributes.ManagedBy === 'BSO') {
        const hideTime = attributes.HideDate ? new Date(attributes.HideDate).getTime() : null
        return hideTime == null || now < hideTime
    }

    const publishEndTime = attributes.PublishEndDate ? new Date(attributes.PublishEndDate).getTime() : null
    return publishEndTime != null && Number.isFinite(publishEndTime) && now < publishEndTime
}

export const isExternalRequestedUnit = (unit: any): boolean => {
    if (!isExternalUnit(unit)) return false

    const statuses = getUnitStatuses(unit)
    if (statuses.includes('Registered')) return true

    // Landlord units are first verified into Approved, then an admin publishes
    // them to the catalogue as Landlord Unit. Keep that pending publication
    // stage in REQUESTED so its Finding Tenant workflow remains actionable.
    return getUnitSourceCategory(unit) === 'landlord'
        && statuses.includes('Approved')
        && !statuses.includes('Landlord Unit')
}

export const isOperationallyActiveExternalUnit = (unit: any): boolean => {
    if (!isExternalUnit(unit)) return false

    const attributes = getAttributes(unit)
    const statuses = getUnitStatuses(unit)
    if (statuses.includes('Archived') || statuses.includes('Registered') || statuses.includes('Drafted') || statuses.includes('Rejected')) return false

    const normalizedStatuses = statuses.map((status) => status.trim().toLowerCase())
    const hasOperationalStatus = normalizedStatuses.some((status) => OPERATIONAL_STATUS_SET.has(status))
    const managedBy = String(attributes?.ManagedBy ?? '').trim().toLowerCase()
    const hasMarketingUse = attributes?.Marketing?.Use != null
    const hasOperationalManagement = (managedBy === 'agent' || managedBy === 'landlord') && hasMarketingUse
    return hasOperationalStatus || hasOperationalManagement
}

export const getExternalUnitBucket = (unit: any, now = Date.now()): ExternalUnitBucket | null => {
    if (!isExternalUnit(unit)) return null
    if (isExternalRequestedUnit(unit)) return 'requested'
    if (isVisibleInAgentCatalog(unit, now)) return 'catalog'
    if (isOperationallyActiveExternalUnit(unit)) return 'operational'
    return 'inactive'
}

export interface MyUnitsContextCounts {
    bsoTotal: number
    bsoCatalogVisible: number
    bsoArchived: number
    externalTotal: number
    externalRequested: number
    externalCatalogVisible: number
    externalOperationalOnly: number
    externalInactive: number
}

export interface MyUnitsListingCounts {
    bsoRentals: number
    bsoSales: number
    externalRentals: number
    externalSales: number
}

export const buildMyUnitsContextCounts = (units: any[], now = Date.now()): MyUnitsContextCounts =>
    (Array.isArray(units) ? units : []).reduce<MyUnitsContextCounts>((counts, unit) => {
        const statuses = getUnitStatuses(unit)
        if (isBsoRentalOrSaleUnit(unit)) {
            counts.bsoTotal += 1
            if (statuses.includes('Archived')) counts.bsoArchived += 1
            if (isVisibleInAgentCatalog(unit, now)) counts.bsoCatalogVisible += 1
            return counts
        }

        const bucket = getExternalUnitBucket(unit, now)
        if (bucket == null) return counts
        counts.externalTotal += 1
        if (bucket === 'requested') counts.externalRequested += 1
        if (bucket === 'catalog') counts.externalCatalogVisible += 1
        if (bucket === 'operational') counts.externalOperationalOnly += 1
        if (bucket === 'inactive') counts.externalInactive += 1
        return counts
    }, {
        bsoTotal: 0,
        bsoCatalogVisible: 0,
        bsoArchived: 0,
        externalTotal: 0,
        externalRequested: 0,
        externalCatalogVisible: 0,
        externalOperationalOnly: 0,
        externalInactive: 0
    })

export const buildMyUnitsListingCounts = (units: any[], now = Date.now()): MyUnitsListingCounts =>
    (Array.isArray(units) ? units : []).reduce<MyUnitsListingCounts>((counts, unit) => {
        const use = getAttributes(unit)?.Marketing?.Use
        if (use !== 'For rent' && use !== 'For sale') return counts

        if (isBsoRentalOrSaleUnit(unit)) {
            if (use === 'For rent') counts.bsoRentals += 1
            if (use === 'For sale') counts.bsoSales += 1
            return counts
        }

        const externalBucket = getExternalUnitBucket(unit, now)
        if (externalBucket === 'requested' || externalBucket === 'catalog' || externalBucket === 'operational') {
            if (use === 'For rent') counts.externalRentals += 1
            if (use === 'For sale') counts.externalSales += 1
        }
        return counts
    }, { bsoRentals: 0, bsoSales: 0, externalRentals: 0, externalSales: 0 })
